/* © CNES */
import { QueryFunctionContext } from '@tanstack/query-core';
import axios from 'axios';
import { NavigateFunction } from 'react-router-dom';
import { apiConfiguration } from '../APIConfiguration';
import { authenticationAPI } from '../authentication/AuthenticateAPI';
import { handleHttpError, handleHttpError409 } from '../common';
import responseMock from '../mock/response_process.json';
import responseDetailsExtractionMock from '../mock/response_process_extraction.json';
import responseDetailsPolarizationMock from '../mock/response_process_polarisation.json';
import responseDetailsValidationMock from '../mock/response_process_validation.json';
import responseQuotasMock from '../mock/response_quotas.json';
import responseUrlMock from '../mock/response_url.json';

/**
 * Get all available Processes list from processing service.
 * @param queryKey the query parameters.
 * @returns a Promise of processes list.
 */
export const getAllProcess = async ({
  queryKey,
}: QueryFunctionContext<
  [string, NavigateFunction, Function]
>): Promise<any> => {
  let res: Response | undefined;
  const [, navigate, setError400] = queryKey;
  if (process.env.NODE_ENV === 'production') {
    res = await fetch(
      `${apiConfiguration.apiBaseUrl}/processing/processes?f=json`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authenticationAPI.getAuthorizationHeaders(),
        },
      }
    );

    handleHttpError(res, navigate, setError400);
  } else {
    res = new Response(JSON.stringify(responseMock));
  }

  return res.json();
};

/**
 * get Process Details information for a specified process id.
 * @param processId : the id of the process to get details info from / example: extract-polarization-s1
 * @returns a Promise containing the details info for a single process.
 */
export const getProcessInfo = async ({
  queryKey,
}: QueryFunctionContext<
  [string, { processId: string | undefined }, NavigateFunction, Function]
>): Promise<any> => {
  let res: Response | undefined;
  const [, params, navigate, setError400] = queryKey;
  if (process.env.NODE_ENV === 'production') {
    res = await fetch(
      `${apiConfiguration.apiBaseUrl}/processing/processes/${params.processId}?f=json`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authenticationAPI.getAuthorizationHeaders(),
        },
      }
    );

    handleHttpError(res, navigate, setError400);
  } else {
    if (params.processId === 'extract-polarization-s1') {
      res = new Response(JSON.stringify(responseDetailsPolarizationMock));
    } else if (params.processId === 'hello-gdh') {
      res = new Response(JSON.stringify(responseDetailsValidationMock));
    } else if (params.processId === 'extract-metadata-s2st') {
      res = new Response(JSON.stringify(responseDetailsExtractionMock));
    } else {
      res = new Response(JSON.stringify(responseDetailsPolarizationMock));
    }
  }

  return res.json();
};

/**
 * Launch the execution of a process with parameters on specified product.
 * @param queryKey the query parameters.
 * @returns a Promise that will return a 200 status if execution is accepted in processing service.
 */
export const launchProcess = async ({
  queryKey,
}: QueryFunctionContext<any>): Promise<any> => {
  let res: Response | undefined;
  const [, processName, launchParams, navigate, setError400, setError409] =
    queryKey;
  if (process.env.NODE_ENV === 'production') {
    res = await fetch(
      `${apiConfiguration.apiBaseUrl}/processing/processes/${processName}/execution`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authenticationAPI.getAuthorizationHeaders(),
          Prefer: 'respond-async',
        },
        body: JSON.stringify(launchParams).replaceAll('\\', ''),
      }
    );

    handleHttpError(res, navigate, setError400);
    handleHttpError409(res, setError409);
  } else {
    res = new Response(JSON.stringify({ test: 'OK' }));
  }
  return res?.json();
};

/**
 * Method returns response containing product url
 * @param jobID the query parameters.
 * @returns a Promise.
 */
export const getProductUrl = async (
  jobID: string,
  navigate: NavigateFunction,
  setError400: Function
): Promise<any> => {
  let res: Response | undefined;
  if (process.env.NODE_ENV === 'production') {
    res = await fetch(
      `${apiConfiguration.apiBaseUrl}/processing/jobs/${jobID}/results?f=json`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authenticationAPI.getAuthorizationHeaders(),
        },
      }
    );
    handleHttpError(res, navigate, setError400);
  } else {
    res = new Response(JSON.stringify(responseUrlMock));
  }
  return res?.json();
};

export const downloadJob = async (
  inputParameter: string,
  product_url: string,
  process_id: string,
  setError400: Function,
  setDownloadProgress: Function,
  navigate: NavigateFunction
): Promise<any> => {
  if (process.env.NODE_ENV === 'production') {
    return axios({
      url: `${product_url}`,
      method: 'GET',
      responseType: 'blob', // important
      headers: {
        'Content-Type': 'application/json',
        ...authenticationAPI.getAuthorizationHeaders(),
      },
      onDownloadProgress: (progressEvent) => {
        if (progressEvent.progress) {
          setDownloadProgress(progressEvent.progress * 100);
        }
      },
    })
      .then((res) => {
        const temporaryDownloadLink = document.createElement('a');
        temporaryDownloadLink.href = URL.createObjectURL(res.data);
        const extension = product_url.split('.').pop();
        temporaryDownloadLink.download = `${inputParameter}_${process_id}.${extension}`;
        document.body.appendChild(temporaryDownloadLink);
        temporaryDownloadLink.click();
        temporaryDownloadLink.parentNode?.removeChild(temporaryDownloadLink);
      })
      .catch((error) => {
        console.error('error while downloading file : ', error);
        handleHttpError(error.response, navigate, setError400);
      })
      .finally(() => setDownloadProgress(null));
  } else {
    return axios({
      url: `../../logo512.png`, //your url
      method: 'GET',
      responseType: 'blob', // important
      onDownloadProgress: (progressEvent) => {
        setDownloadProgress(progressEvent.progress);
      },
    })
      .then((response) => {
        // create file link in browser's memory
        const href = URL.createObjectURL(response.data);
        // create "a" HTML element with href to file & click
        const link = document.createElement('a');
        link.href = href;
        link.setAttribute('download', 'logo512.png'); //or any other extension
        document.body.appendChild(link);
        link.click();

        // // clean up "a" element & remove ObjectURL
        document.body.removeChild(link);
        URL.revokeObjectURL(href);
      })
      .catch((error) => {
        console.error('error while downloading file : ', error);
        handleHttpError(error.response, navigate, setError400);
      })
      .finally(() => setDownloadProgress(null));
  }
};

export const deleteJob = async (
  jobID: string,
  navigate: NavigateFunction,
  setError400: Function
): Promise<any> => {
  let res: Response | undefined;
  if (process.env.NODE_ENV === 'production') {
    res = await fetch(
      `${apiConfiguration.apiBaseUrl}/processing/jobs/${jobID}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...authenticationAPI.getAuthorizationHeaders(),
        },
      }
    );

    handleHttpError(res, navigate, setError400);
  } else {
    res = new Response(JSON.stringify({ test: 'OK' }));
  }
  return res?.json();
};

/**
 * Quotas checking from quotas checking service.
 * @param queryKey the query parameters.
 * @returns a quotas number.
 */
export const getQuotasProcess = async ({
  queryKey,
}: QueryFunctionContext<[string, NavigateFunction, Function]>): Promise<{
  maxQuota: number;
  remainingQuota: number;
}> => {
  let res: Response | undefined;
  const [, navigate, setError400] = queryKey;
  if (process.env.NODE_ENV === 'production') {
    res = await fetch(
      `${apiConfiguration.apiBaseUrl}/processing/quotasProcesses/getRemainingQuotas`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authenticationAPI.getAuthorizationHeaders(),
        },
      }
    );

    handleHttpError(res, navigate, setError400);
  } else {
    res = new Response(JSON.stringify(responseQuotasMock));
  }
  return res.json();
};

/**
 * Quotas checking from quotas checking service.
 * @param queryKey the query parameters.
 * @returns a quotas number.
 */
export const getDownloadQuotas = async ({
  queryKey,
}: QueryFunctionContext<[string, NavigateFunction, Function]>): Promise<{
  maxQuota: number;
  remainingQuota: number;
}> => {
  let res: Response | undefined;
  const [, navigate, setError400] = queryKey;
  if (process.env.NODE_ENV === 'production') {
    res = await fetch(
      `${apiConfiguration.apiBaseUrl}/processing/download/get`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authenticationAPI.getAuthorizationHeaders(),
        },
      }
    );

    handleHttpError(res, navigate, setError400);
  } else {
    res = new Response(JSON.stringify(responseQuotasMock));
  }
  return res.json();
};
